import { OfChatMediasService } from '@api'
import {
  Domain,
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'

import { $creatorId } from '@/models/creators.ts'
import { $isMediaCloningEnabled } from '@/models/features.ts'
import { showToastSuccess } from '@/models/toasts.ts'
import { mediaService } from '@/modules/chatting/service/media.ts'
import type { CloneMediaJobResponse } from '@/modules/chatting/types.ts'
import { handleError } from '@/utils/error'

export const $cloneToOriginalRegistry = createStore(new Map<number, number>(), {
  name: 'clone to original medias registry',
})

export const $originalToCloneRegistry = createStore(
  new Map<number, number[]>(),
  {
    name: 'original to clone medias registry',
  },
)

export const startClonedMediaByOriginalsPolling = createEvent(
  "start cloned media's polling",
)
export const stopClonedMediaByOriginalsPolling = createEvent(
  "stop cloned media's polling",
)

export const $isClonedMediaByOriginalsPolling = createStore(false, {
  name: 'is cloned media by originals polling',
})
  .on(startClonedMediaByOriginalsPolling, () => true)
  .reset(stopClonedMediaByOriginalsPolling)

export const getClonedMediaByOriginalsFx = attach({
  name: 'get cloned media by originals',
  source: {
    creatorId: $creatorId,
    isMediaCloningEnabled: $isMediaCloningEnabled,
  },
  mapParams: (_: void, { creatorId, isMediaCloningEnabled }) => ({
    creatorId: creatorId!,
    isMediaCloningEnabled,
  }),
  effect: createEffect(
    async ({
      creatorId,
      isMediaCloningEnabled,
    }: {
      creatorId: number
      isMediaCloningEnabled: boolean
    }) => {
      if (!isMediaCloningEnabled) {
        return { clonesByOriginal: {} }
      }

      return OfChatMediasService.getClonedMedia({ creatorId })
    },
  ),
})

sample({
  clock: getClonedMediaByOriginalsFx.doneData,
  fn: ({ clonesByOriginal }) =>
    Object.entries(clonesByOriginal!).reduce((result, [original, clones]) => {
      clones.forEach((clone) => {
        result.set(clone, Number(original))
      })

      return result
    }, new Map<number, number>()),
  target: $cloneToOriginalRegistry,
})

sample({
  clock: getClonedMediaByOriginalsFx.doneData,
  fn: ({ clonesByOriginal }) =>
    Object.entries(clonesByOriginal!).reduce((result, [original, clones]) => {
      result.set(Number(original), clones)

      return result
    }, new Map<number, number[]>()),
  target: $originalToCloneRegistry,
})

sample({
  clock: getClonedMediaByOriginalsFx.failData,
  fn: () => new Map<number, number>(),
  target: $cloneToOriginalRegistry,
})

sample({
  clock: getClonedMediaByOriginalsFx.failData,
  fn: () => new Map<number, number[]>(),
  target: $originalToCloneRegistry,
})

// poll request every 10 secs
let timeoutId: Nullable<number> = null

combine($isMediaCloningEnabled, $isClonedMediaByOriginalsPolling).watch(
  ([isCloningEnabled, isPollingEnabled]) => {
    if (isCloningEnabled && isPollingEnabled) {
      getClonedMediaByOriginalsFx()

      timeoutId = window.setInterval(getClonedMediaByOriginalsFx, 10000)
    } else if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  },
)

export const createUserCloneMediaModel = (userDomain: Domain) => {
  const getCloneMediaJobStatusFx = userDomain.createEffect({
    name: "get clone media job's status",
    handler: async ({
      jobId,
      creatorId,
    }: {
      jobId: string
      creatorId: number
    }) => {
      const response = await mediaService.getCloneMediaJobStatus(
        creatorId,
        jobId,
      )

      if (response?.done) {
        getClonedMediaByOriginalsFx()
      } else {
        window.setTimeout(getCloneMediaJobStatusFx, 1500, { creatorId, jobId })
      }

      return response
    },
  })

  getCloneMediaJobStatusFx.failData.watch((error) => {
    handleError(error, 'Failed to clone media')
  })

  const cloneMediaFx = attach({
    name: 'clone media',
    source: {
      creatorId: $creatorId,
    },
    mapParams: (mediaId: number, { creatorId }) => ({
      mediaId,
      creatorId: creatorId!,
    }),
    effect: userDomain.createEffect(
      async ({
        mediaId,
        creatorId,
      }: {
        mediaId: number
        creatorId: number
      }) => {
        const response = await mediaService.cloneMedia(creatorId, mediaId)
        const jobId = response?.id

        if (response && !response.done) {
          window.setTimeout(getCloneMediaJobStatusFx, 1500, {
            creatorId,
            jobId,
          })
        }

        return response
      },
    ),
  })

  cloneMediaFx.failData.watch((error) => {
    handleError(error, 'Failed to clone media')
  })

  const removeCloneByMediaId = userDomain.createEvent<number>(
    'remove clone job by media id',
  )
  const resetCloneMediaJobs = userDomain.createEvent('reset clone media jobs')

  const $jobRegistry = userDomain
    .createStore<Record<string, CloneMediaJobResponse>>(
      {},
      { name: 'media clone job registry' },
    )
    .on(removeCloneByMediaId, (store, mediaId) => {
      const job = Object.values(store).find(
        ({ media }) => media?.id === mediaId,
      )

      if (job) {
        const newValue = { ...store }

        delete newValue[job.id]

        return newValue
      } else {
        return store
      }
    })
    .reset(resetCloneMediaJobs)

  sample({
    clock: getCloneMediaJobStatusFx.doneData,
    source: { jobRegistry: $jobRegistry },
    filter: ({ jobRegistry }, response) => {
      return jobRegistry[response.id]?.status !== response.status
    },
    fn: (_, response) =>
      response.status === 'Done'
        ? 'Successfully cloned and attached media!'
        : response.status,
    target: showToastSuccess,
  })

  sample({
    clock: cloneMediaFx.doneData,
    source: { jobRegistry: $jobRegistry },
    filter: ({ jobRegistry }, response) => {
      return jobRegistry[response.id]?.status !== response.status
    },
    fn: (_, response) => response.status,
    target: showToastSuccess,
  })

  sample({
    clock: [cloneMediaFx.doneData, getCloneMediaJobStatusFx.doneData],
    source: { prev: $jobRegistry },
    fn: ({ prev }, response) => {
      const jobId = response?.id

      if (jobId) {
        return {
          ...prev,
          [jobId]: response,
        }
      }

      return prev
    },
    target: $jobRegistry,
  })

  const $cloneMediaJobs = $jobRegistry.map((jobs) => {
    return Object.values(jobs)
  })

  const $cloneMediaJobsInProgress = $cloneMediaJobs.map((jobs) => {
    return jobs.filter((job) => !job.done)
  })

  const $cloneMediaJobsFinished = $cloneMediaJobs.map((jobs) => {
    return jobs.filter((job) => job.done)
  })

  return {
    effects: {
      getCloneMediaJobStatusFx,
      cloneMediaFx,
      getClonedMediaByOriginalsFx,
    },
    events: {
      removeCloneByMediaId,
      resetCloneMediaJobs,
    },
    stores: {
      $jobRegistry,
      $cloneMediaJobs,
      $cloneMediaJobsInProgress,
      $cloneMediaJobsFinished,
    },
  }
}
